import { Onboarding } from "sdk/onboardings";
import { routes } from "~/constants";
import { emailTrackerStem } from "~/OnboardingApp/events";
import {
  postpartumOption,
  pregnantOption,
  shouldSeeDueDatePage,
} from "~/OnboardingApp/utils";
import {
  emptyPhase,
  logisticsPhase,
  welcomePhase,
  yourFertilityJourneyPhase,
} from "./constants";

type OnboardingMeta = {
  analyticsStem: string;
  canRevisit: (onboarding: Partial<Onboarding>) => boolean;
  excludeFromBack?: boolean;
  excludeFromProgress?: boolean;
  excludeNav?: boolean;
  isAnswered: (onboarding: Partial<Onboarding>) => boolean;
  isOptional?: boolean;
  isPublic?: boolean;
  phase: string;
};
export interface MetaByPath {
  [key: string]: OnboardingMeta;
}

export const metaByPath: MetaByPath = {
  [routes.onboardingRoot]: {
    analyticsStem: "onboarding-root-page",
    canRevisit: () => false,
    isPublic: false,
    isAnswered: () => true,
    phase: welcomePhase,
  },
  // [routes.interstitialWelcome]: {
  //   analyticsStem: "onboarding-welcome-page",
  //   canRevisit: () => true,
  //   isPublic: true,
  //   isAnswered: () => true,
  //   isOptional: true,
  //   phase: welcomePhase,
  // },
  [routes.questionName]: {
    analyticsStem: "onboarding-name-page",
    canRevisit: () => true,
    isPublic: true,
    isAnswered: ({ firstName, lastName }) => !!(firstName && lastName),
    phase: welcomePhase,
  },
  [routes.questionEmail]: {
    analyticsStem: emailTrackerStem,
    isPublic: true,
    canRevisit: ({ email }) => !email,
    isAnswered: ({ email }) => !!email,
    phase: welcomePhase,
  },
  [routes.questionHowLong]: {
    analyticsStem: "onboarding-how-long-page",
    canRevisit: () => true,
    isAnswered: ({ lengthOfTimeTtc }) => !!lengthOfTimeTtc,
    phase: yourFertilityJourneyPhase,
  },
  [routes.questionDueDate]: {
    analyticsStem: "onboarding-due-date-page",
    canRevisit: ({ lengthOfTimeTtc }) =>
      !!lengthOfTimeTtc && shouldSeeDueDatePage(lengthOfTimeTtc),
    isAnswered: ({ dueDate, lengthOfTimeTtc }) =>
      !!dueDate?.length ||
      (!!lengthOfTimeTtc && !shouldSeeDueDatePage(lengthOfTimeTtc)),
    phase: yourFertilityJourneyPhase,
    isOptional: true,
  },
  [routes.questionProvidersSeen]: {
    analyticsStem: "onboarding-providers-seen-page",
    canRevisit: () => true,
    isAnswered: ({ providersSeen }) => !!providersSeen?.length,
    phase: yourFertilityJourneyPhase,
  },
  // [routes.questionClinicSatisfaction]: {
  //   analyticsStem: "onboarding-clinic-satisfaction-page",
  //   canRevisit: ({ providersSeen }) => !!providersSeen?.includes(reiClinic),
  //   isAnswered: ({ clinicSatisfaction, providersSeen }) =>
  //     (!!providersSeen?.length && !providersSeen?.includes(reiClinic)) ||
  //     !!clinicSatisfaction,
  //   phase: yourFertilityJourneyPhase,
  // },
  [routes.questionJourneyLooksLike]: {
    analyticsStem: "onboarding-journey-looks-like-page",
    canRevisit: ({ lengthOfTimeTtc }) =>
      lengthOfTimeTtc != pregnantOption && lengthOfTimeTtc != postpartumOption,
    isAnswered: ({ lengthOfTimeTtc, journeyLooksLike }) =>
      lengthOfTimeTtc == pregnantOption ||
      lengthOfTimeTtc == postpartumOption ||
      !!journeyLooksLike?.length,
    phase: yourFertilityJourneyPhase,
  },
  [routes.questionNextMilestone]: {
    analyticsStem: "onboarding-next-milestone-page",
    canRevisit: ({ journeyLooksLike }) => {
      const selectedJourney = journeyLooksLike?.[0] || "";
      return selectedJourney !== "Other";
    },
    isAnswered: ({ upcomingMilestone, journeyLooksLike }) => {
      const selectedJourney = journeyLooksLike?.[0] || "";
      return !!upcomingMilestone?.length || selectedJourney === "Other";
    },
    phase: yourFertilityJourneyPhase,
  },

  [routes.questionPartner]: {
    analyticsStem: "onboarding-partner-page",
    canRevisit: () => true,
    isAnswered: ({ partner }) => !!partner,
    phase: yourFertilityJourneyPhase,
  },

  [routes.questionSupportNeeds]: {
    analyticsStem: "onboarding-support-needs-page",
    canRevisit: () => true,
    isAnswered: ({ supportNeeds }) => !!supportNeeds?.length,
    phase: yourFertilityJourneyPhase,
  },

  [routes.questionMostChallenging]: {
    analyticsStem: "onboarding-most-challenging-page",
    canRevisit: () => true,
    isAnswered: ({ biggestChallenge }) => !!biggestChallenge,
    isOptional: true,
    phase: yourFertilityJourneyPhase,
  },
  
  [routes.questionClinicSatisfaction]: {
    analyticsStem: "onboarding-clinic-satisfaction-page",
    canRevisit: () => true,
    isAnswered: ({ clinicSatisfaction }) => !!clinicSatisfaction,
    phase: yourFertilityJourneyPhase,
  },
  // [routes.questionHowSupported]: {
  //   analyticsStem: "onboarding-how-supported-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howSupported }) => !!howSupported,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.questionHowOptimistic]: {
  //   analyticsStem: "onboarding-how-optimistic-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howOptimistic }) => !!howOptimistic,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.questionHowAnxious]: {
  //   analyticsStem: "onboarding-how-anxious-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howAnxious }) => !!howAnxious,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.questionHowGrateful]: {
  //   analyticsStem: "onboarding-how-grateful-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howGrateful }) => !!howGrateful,
  //   phase: yourFertilityJourneyPhase,
  // },
  // [routes.interstitialLogistics]: {
  //   analyticsStem: "onboarding-logistics-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ howGrateful }) => !!howGrateful,
  //   isOptional: true,
  //   phase: emptyPhase,
  // },
  [routes.questionBirthday]: {
    analyticsStem: "onboarding-birthday-page",
    canRevisit: () => true,
    isAnswered: ({ bornOn }) => !!bornOn,
    phase: logisticsPhase,
  },
  // [routes.questionEmployer]: {
  //   analyticsStem: "onboarding-employer-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ employer }) => !!employer,
  //   isOptional: true,
  //   phase: logisticsPhase,
  // },
  // [routes.questionCoverage]: {
  //   analyticsStem: "onboarding-coverage-page",
  //   canRevisit: ({ lengthOfTimeTtc }) =>
  //     lengthOfTimeTtc != pregnantOption && lengthOfTimeTtc != postpartumOption,
  //   isAnswered: ({ lengthOfTimeTtc, fertilityCoverage }) =>
  //     lengthOfTimeTtc == pregnantOption ||
  //     lengthOfTimeTtc == postpartumOption ||
  //     !!fertilityCoverage?.length,
  //   phase: logisticsPhase,
  // },
  [routes.questionPhone]: {
    analyticsStem: "onboarding-phone-page",
    canRevisit: () => true,
    isAnswered: ({ international, phone }) => international || !!phone,
    phase: logisticsPhase,
  },
  // [routes.questionZip]: {
  //   analyticsStem: "onboarding-zip-page",
  //   canRevisit: () => true,
  //   isAnswered: ({ zipCode }) => !!zipCode,
  //   phase: logisticsPhase,
  // },
  [routes.questionHowDidYouHear]: {
    analyticsStem: "onboarding-how-did-you-hear-page",
    canRevisit: () => true,
    isAnswered: ({ howDidYouHear }) => !!howDidYouHear,
    phase: logisticsPhase,
  },
  [routes.interstitialThanksForSharing]: {
    analyticsStem: "onboarding-thanks-for-sharing-page",
    canRevisit: () => true,
    isAnswered: ({ howDidYouHear }) => !!howDidYouHear,
    phase: emptyPhase,
    excludeFromBack: true,
    excludeFromProgress: true,
    excludeNav: true,
  },
};
